(ns client-portal.client.reports.individual.views
  (:require [clojure.string :as s]
            [client-portal.client.layout :as l]
            [client-portal.client.reports.individual.data :as data]
            [client-portal.client.reports.components.ai-summary :as ai-summary]
            [client-portal.client.reports.components.category-score-gauge-with-line-chart :refer [category-score-gauge-with-line-chart]]
            [client-portal.client.reports.components.key-metrics-stacked-bar-chart :refer [key-metrics-stacked-bar-chart]]
            [client-portal.client.reports.components.day-parts-toggle-group :refer [day-parts-toggle-group]]
            [client-portal.client.reports.components.table :refer [table]]
            [client-portal.client.routes :as routes]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [shared.core.components.components :as c]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.utils.currency :as currency]
            [shared.core.utils.sql :as sql]
            [shared.core.util :as util]
            [client-portal.client.voice-insights.charts.views :as charts]
            [client-portal.client.voice-insights.charts.data :as charts.d]
            [shared.core.components.recharts.util :as recharts.util]
            ["react-markdown" :default ReactMarkdown]
            ["remark-gfm" :default remarkGfm]))

(declare component-grid)

(defn- format-value-card-value [{:keys [value-type country-code template value]}]
  (try
    (let [formatted (condp = value-type
                      "currency" (currency/currency-integer->readable-currency (or country-code "US") value)
                      "percent" (str (util/to-precision-num (* value 100) :precision 2) "%")
                      "text" (str value)
                      (util/readable-number value))
          v (if (not-empty template)
              (s/replace template "{value}" (str formatted))
              formatted)]
      (if (and (not (string? v)) (NaN? v)) value v))
    (catch :default _
      value)))

(defn- value-card-values
  "The card's primary and secondary values, read from a result `row` by NAME. A
   row's key order doesn't survive the trip here (its jsonb round trip re-orders
   the keys), so the primary value is the column aliased `value` and the
   secondary is whichever column is left — never `first`/`second` of the row."
  [row]
  (let [value-key (or (some #(when (= "value" (name %)) %) (keys row))
                      (first (keys row)))]
    [(get row value-key)
     (first (vals (dissoc row value-key)))]))

(defn value-card [_]
  (let [size* (re/ratom nil)
        size-ref (util/size-ref size*)]
    (fn [{:keys [title description value-type color template secondary-value-config country-code data ai-summary]}]
      (let [{secondary-value-type :value-type
             secondary-value-color :color
             secondary-template :template} secondary-value-config
            data? (not-empty (util/when-not-loading data))
            row (when data?
                  (first data))
            [value secondary-value] (value-card-values row)
            width (:width @size*)]
        ;; No `height: 100%` — the grid cell already stretches the card, and with it iOS Safari
        ;; keeps the card's first-layout height, so content that grows afterward (the sized-up
        ;; value, text rewrapped once Roboto swaps in) spills onto the bottom border.
        [c/interactive
         alc/card {:title title
                   :subtitle description}
         [ai-summary/ai-summary {:summary ai-summary}]
         (cond
           (util/loading? data)
           [alc/up-loader-with-label {:label "Loading Data..."
                                      :style {:padding 60}}]

           value
           ;; Sized from its measured width, not `cqi` — iOS Safari lays the card out with a
           ;; stale container-unit font size and the value spills out. Side margin, not
           ;; padding, so the measured width is the content width.
           [:div {:ref size-ref
                  :style {:flex 1
                          :display :flex
                          :flex-direction :column
                          :align-items :center
                          :justify-content :center
                          :gap 6
                          :text-align :center
                          :visibility (when-not width :hidden)
                          :margin "0 10px"
                          :padding (if secondary-value
                                     "10px 0"
                                     "10px 0 20px")}}
            (let [formatted (format-value-card-value {:value-type value-type
                                                      :country-code country-code
                                                      :template template
                                                      :value value})]
              ;; ~0.6em per character, so 1.5 × width / chars fills about 90% of the card. Below
              ;; 22px it wraps instead, shrinking further only so the longest word still fits.
              [:div {:style {:font-size (when width
                                          (->> (s/split (str formatted) #"\s+")
                                               (map #(/ (* 1.9 width) (max 5 (count %))))
                                               (apply min)
                                               (min 22)
                                               (max (/ (* 1.5 width) (max 5 (count (str formatted)))))
                                               (min 50)))
                             :line-height 1.2
                             :font-weight style/font-medium-weight
                             :color (or color (style/alc-color "Text/primary"))
                             :text-align :center
                             :text-wrap :balance
                             :max-width "100%"}}
               formatted])
            (when secondary-value
              [:div {:style {:font-size (some->> width (* 0.08) (min 20) (max 13))
                             :line-height 1.2
                             :font-weight style/font-regular-weight
                             :color (or secondary-value-color (style/alc-color "Text/secondary"))
                             :text-align :center
                             :display "-webkit-box"
                             :-webkit-box-orient "vertical"
                             :-webkit-line-clamp 3
                             :overflow :hidden
                             :max-width "100%"}}
               (format-value-card-value {:value-type secondary-value-type
                                         :country-code country-code
                                         :template secondary-template
                                         :value secondary-value})])]

           :else
           [empty-state/no-data-card {:style {:padding 20}}])]))))

(defn query-card
  "The SQL a component actually ran, in place of the component itself. Same card
   shell as `value-card`/`chart-card` so swapping between them doesn't move the grid."
  [{:keys [title query]}]
  [alc/card {:title (or (not-empty title) "Query")
             :subtitle "Executed query"
             :style {:height "100%"}}
   [:pre {:style {:background-color (style/alc-color "Background/secondary")
                  :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                  :border-radius 8
                  :color (style/alc-color "Text/primary")
                  :font-family "ui-monospace, SFMono-Regular, Menlo, monospace"
                  :font-size 12
                  :line-height 1.5
                  :margin 0
                  :max-height 600
                  :overflow :auto
                  :padding 12
                  :white-space :pre
                  :width "100%"}}
    (sql/format-query query)]])

(defn report-tabs [raw-config default-filter-values report-filters component-flags]
  (let [raw-cmp-config (get raw-config "configuration")
        tabs (get raw-cmp-config "tabs")
        cmp-config (util/->kebab-case-keywords raw-config)]
    [alc/up-tabs (assoc cmp-config
                   :style (merge {:height "100%"}
                                 (:style cmp-config))
                   :list-style (merge {:background-color (style/alc-color "Background/primary")
                                       :position :sticky
                                       :top 60
                                       :left 0}
                                      (:list-style cmp-config))
                   :tabs (mapv (fn [tab]
                                 (assoc (util/->kebab-case-keywords tab)
                                   :content [[:div {:style {:display :flex
                                                            :align-items :center
                                                            :justify-content :center
                                                            :padding 40
                                                            :height "100%"
                                                            :width "100%"}}
                                              [component-grid {:configuration {"content" (get tab "content")}
                                                               :default-filter-values default-filter-values
                                                               :report-filters report-filters
                                                               :component-flags component-flags}]]]))
                               tabs))]))

(defn grid-cmp [_]
  (let [report-id- (re/subscribe [:reports/current-report-id-])
        admin?- (re/subscribe [:account/admin-role?-])
        hover?* (re/ratom false)
        show-query?* (re/ratom false)
        renders-content?* (re/ratom true)
        content-observer* (atom nil)
        observe-content (fn [el]
                          (some-> @content-observer* (.disconnect))
                          (reset! content-observer* nil)
                          (when el
                            (let [check #(reset! renders-content?* (or (not (s/blank? (.-textContent el)))
                                                                       (some? (.querySelector el "svg, canvas, img"))))]
                              (check)
                              (reset! content-observer* (doto (js/MutationObserver. check)
                                                          (.observe el #js {:childList true
                                                                            :subtree true
                                                                            :characterData true}))))))]
    (fn [{:keys [raw-cmp-config view-only? report-id default-filter-values report-filters min-width
                 component-flags]}]
      (let [cmp-config (util/->kebab-case-keywords raw-cmp-config)]
        (when-let [cmp-type (:component-type cmp-config)]
          (let [report-id (or report-id @report-id-)
                cmp-content (get raw-cmp-config "content")
                cmp-args (cond-> (:configuration cmp-config)
                                 view-only?
                                 (assoc :on-click #()
                                        :on-value-change #())
                                 ;; Report-level default filter values ride along so a chart's
                                 ;; session double-click can seed the drill-in query params
                                 ;; (filling only keys the current route doesn't already set).
                                 default-filter-values
                                 (assoc :default-filter-values default-filter-values)

                                 ;; The drill-in drops the params of the filters it hides.
                                 report-filters
                                 (assoc :report-filters report-filters))
                content (into (condp = cmp-type
                                "chart" [charts/chart-from-config cmp-args]
                                "chart-card" (let [sankey? (= (:chart-type cmp-args) "Sankey")
                                                   chart-data (cond-> (get-in raw-cmp-config ["configuration" "data"])
                                                                      ;; Sankey needs {nodes links}; build it from the flat query rows
                                                                      ;; (keyword-keyed) using the authored source/target/value columns.
                                                                      sankey?
                                                                      (#(recharts.util/build-sankey-data
                                                                          {:data (mapv util/->kebab-case-keywords %)
                                                                           :source-key (keyword (:source-key cmp-args))
                                                                           :target-key (keyword (:target-key cmp-args))
                                                                           :value-key (keyword (:value-key cmp-args))}))
                                                                      (:show-time-period-toggle? cmp-args)
                                                                      (charts.d/->formatted-chart-data :grouping-key (:time-period-key cmp-args)))
                                                   config-overrides (:config-overrides cmp-args)]
                                               [charts/chart-card (assoc cmp-args
                                                                    ;; Only a local key (per-chart form state) — report data is
                                                                    ;; always server-resolved, so never let the card self-fetch it.
                                                                    :chart-id (or (:chart-id cmp-args)
                                                                                  (str report-id
                                                                                       "-"
                                                                                       (if-let [title (:title cmp-args)]
                                                                                         (util/->kebab-case-string title)
                                                                                         (util/random-uuid-string))))
                                                                    :self-load? false
                                                                    :data chart-data
                                                                    ;; Raw: keyed by the values it labels, which kebab-casing would mangle.
                                                                    :labels (get-in raw-cmp-config ["configuration" "labels"])
                                                                    :config-overrides (cond-> config-overrides
                                                                                              (:data-keys-config config-overrides)
                                                                                              (assoc :data-keys-config (get-in raw-cmp-config ["configuration"
                                                                                                                                               "configOverrides"
                                                                                                                                               "dataKeysConfig"]))))])
                                "component-grid" [component-grid (assoc raw-cmp-config
                                                                   :configuration (get raw-cmp-config "configuration")
                                                                   :fill-height? true
                                                                   :default-filter-values default-filter-values
                                                                   :report-filters report-filters
                                                                   :component-flags component-flags)]
                                "markdown" [:div {:class-name "markdown-body"
                                                  :style {:color (style/alc-color "Text/primary")
                                                          :font-size 16
                                                          :line-height 1.4
                                                          :width "100%"}}
                                            [:> ReactMarkdown
                                             (merge {:remarkPlugins [remarkGfm]}
                                                    cmp-args)]]
                                "section-header" [l/section-header (assoc cmp-args :data (get-in raw-cmp-config ["configuration" "data"]))]
                                "tabs" [report-tabs raw-cmp-config default-filter-values report-filters component-flags]
                                "value-card" [value-card (assoc cmp-args :data (get-in raw-cmp-config ["configuration" "data"]))]
                                "table" [table (assoc cmp-args :data (get-in raw-cmp-config ["configuration" "data"]))]
                                "category-score-gauge-with-line-chart" [category-score-gauge-with-line-chart cmp-args]
                                "key-metrics-stacked-bar-chart" [key-metrics-stacked-bar-chart cmp-args]
                                "day-parts-toggle-group" [:div {:style {:height :auto}}
                                                          [day-parts-toggle-group cmp-args]]
                                [(keyword cmp-type) cmp-args])
                              (cond
                                (and (coll? cmp-content)
                                     (not-empty cmp-content)
                                     (every? sequential? cmp-content))
                                [[component-grid {:configuration {"content" cmp-content}
                                                  :default-filter-values default-filter-values
                                                  :report-filters report-filters
                                                  :component-flags component-flags}]]

                                (coll? cmp-content)
                                (mapv (fn [conf] [grid-cmp {:raw-cmp-config conf
                                                            :default-filter-values default-filter-values
                                                            :report-filters report-filters
                                                            :component-flags component-flags}]) cmp-content)

                                :else
                                cmp-content))]
            (when (not-empty content)
              ;; `:query` is the actually-executed SQL, attached by the resolver for
              ;; admins only (never present for non-admins). Hover-reveal the query
              ;; actions when it's there.
              (let [query (:query cmp-args)
                    query-actions? (and @admin?- (not-empty query))
                    ;; Once the query is showing, keep the actions up without a hover —
                    ;; otherwise the only way back to the card is hidden.
                    showing-query? (and query-actions? @show-query?*)
                    flag (get component-flags (get raw-cmp-config "id"))
                    flag-hue (case flag
                               :added (style/alc-color "Common/success_hex")
                               :updated (style/alc-color "Common/active")
                               nil)]
                [c/interactive
                 :div {:style {:flex 1
                               :display :grid
                               :grid-template-columns "minmax(0, 1fr)"
                               :min-width min-width
                               :position :relative}
                       :hover?* hover?*}
                 ;; Sits exactly over a card's own 1px border, recoloring it. Skipped when
                 ;; the component renders nothing (e.g. a toggle group with no day parts).
                 (when (and flag-hue @renders-content?*)
                   [:div {:style {:border (str "1px solid " (style/mix-color flag-hue 45))
                                  :border-radius 8
                                  :inset 0
                                  :pointer-events :none
                                  :position :absolute
                                  :z-index 2}}
                    (when @hover?*
                      [:div {:style {:background-color (style/mix-color flag-hue 25 (style/alc-color "Background/card_hex"))
                                     :border (str "1px solid " (style/mix-color flag-hue 45))
                                     :border-radius 9
                                     :color (style/alc-color "Text/primary")
                                     :font-size 11
                                     :font-weight 600
                                     :left 12
                                     :line-height "18px"
                                     :padding "0 8px"
                                     :position :absolute
                                     :top -0.5
                                     :transform "translateY(-50%)"}}
                       (if (= :added flag) "New" "Updated")])])
                 (when (and query-actions? (or @hover?* showing-query?))
                   [:div {:style {:display :flex
                                  :align-items :center
                                  :gap 8
                                  :position :absolute
                                  :top 19
                                  ;; Clear the chart-card's view-mode toggle, which the
                                  ;; query card doesn't have.
                                  :right (if (and (= cmp-type "chart-card")
                                                  (not showing-query?)
                                                  (not (false? (:show-view-mode-toggle? cmp-args))))
                                           132 19)
                                  :z-index 2}}
                    [alc/tooltip
                     {:target-content [alc/up-button {:icon-type :CopyIcon
                                                      :on-click #(re/dispatch-sync [:core/copy-to-clipboard query {:notify? true}])}]
                      :tooltip-content "Copy query"}]
                    [alc/tooltip
                     {:target-content [alc/up-button {:icon-type (if showing-query? :CardsIcon :CodeIcon)
                                                      :on-click #(swap! show-query?* not)}]
                      :tooltip-content (if showing-query? "Show card" "Show query")}]])
                 (cond
                   showing-query? [query-card {:title (:title cmp-args)
                                               :query query}]
                   ;; Only the builder passes flags; `display: contents` keeps the
                   ;; observed wrapper out of the layout.
                   component-flags [:div {:ref observe-content
                                          :style {:display :contents}}
                                    content]
                   :else content)]))))))))

(defn component-grid [{:keys [configuration no-content-message view-only? report-id default-filter-values root-id
                              report-filters component-flags fill-height?]}]
  (let [content (get configuration "content")
        ;; Read once at the report root; threaded down so nested charts inherit it.
        default-filter-values (or default-filter-values (get configuration "defaultFilterValues"))]
    (if (empty? content)
      [empty-state/no-data-card
       {:text (or no-content-message "No content")
        :material-icon-type :inventory_2}]
      (into [:div {:id root-id
                   :class-name style/stack-container-class-name
                   :style {:flex 1
                           :display :flex
                           :flex-direction :column
                           :gap (if (not-empty component-flags) 24 12)
                           :min-height "100%"
                           :width "100%"}}]
            (mapv (fn [row-content]
                    ;; A row of value cards wraps onto more lines rather than squeezing
                    ;; each card below a readable width.
                    (let [value-cards? (every? #(-> % (util/->kebab-case-keyword-top-level) :component-type (= "value-card"))
                                               row-content)]
                      (->> row-content
                           (mapv (fn [raw-cmp-config] [grid-cmp {:raw-cmp-config raw-cmp-config
                                                                 :view-only? view-only?
                                                                 :report-id report-id
                                                                 :default-filter-values default-filter-values
                                                                 :report-filters report-filters
                                                                 :component-flags component-flags
                                                                 :min-width (when value-cards? 160)}]))
                           (into [:div {:class-name (when-not value-cards? style/stack-when-narrow-class-name)
                                        :style {:flex (if fill-height? "1 1 auto" "0 1 auto")
                                                :display :flex
                                                :flex-wrap (if value-cards? :wrap :nowrap)
                                                :column-gap 12
                                                :row-gap (if (not-empty component-flags) 24 12)
                                                :width "100%"}}]))))
                  content)))))

(defn report-details [args]
  [component-grid (assoc args
                    :no-content-message "Report empty"
                    :root-id data/pdf-grid-root-id)])

(defn root [_]
  (let [scheduled-refresh* (atom nil)
        seeded-defaults* (atom nil)
        day-parts- (re/subscribe [:day-parts/by-current-concept-id-])
        qps- (re/subscribe [:router/current-query-params-])
        nav-takeover-route?- (re/subscribe [:reports/nav-takeover-route?-])
        edit-route-access?- (re/subscribe [:account/has-roles-access?- (:roles (get routes/routes :reports/individual-edit))])]
    (re/create-class
      {:component-did-mount (fn [this]
                              (re/dispatch [:reports/initialize-individual])
                              ;; Deep-link / refresh on the report session route
                              ;; (/reports/i/:report-id/:session-id): reopen the chat panel
                              ;; and load that session.
                              (let [{:keys [report-id session-id]} (re/props this)]
                                (when (not-empty session-id)
                                  (re/dispatch [:ai-agent/resume-report-chat {:report-id report-id
                                                                              :session-id session-id}]))))
       :component-will-unmount (fn [_]
                                 (re/dispatch [:reports/clear-refresh-timer])
                                 (re/dispatch [:layout/hide-panel-right])
                                 (reset! scheduled-refresh* nil))
       :reagent-render
       (fn [{:keys [report-id all-reports-button?]
             :or {all-reports-button? true}
             :as args}]
         (if (and (:dp @qps-) (or (util/loading? @day-parts-) (nil? @day-parts-)))
           [l/logged-in-with-header-nav-vi args
            [l/standard-content-container
             [empty-state/loading-report-card]]]
           (let [report @(re/subscribe [:reports/individual-data- report-id])
                 loading? (util/loading? report)
                 ready? (and (not loading?) (not (nil? report)) (seq report))
                 ;; Fetched ahead of the report so the chrome resolves without waiting on
                 ;; its queries. Nil until it lands, which holds every configurable
                 ;; filter back rather than showing one the report turns out to hide.
                 settings (util/when-not-loading @(re/subscribe [:reports/settings- report-id]))
                 ;; The report's own setting decides, with nothing overriding it — a ?lid
                 ;; in the URL is left alone but neither shows the control nor reaches the
                 ;; query when the report hides location.
                 filters (when settings (data/report-filters (:filters settings)))
                 target-concept-ids (:target-concept-ids settings)
                 ;; Owner or direct share — an organization share is read-only, and the
                 ;; edit-session endpoint enforces the same rule.
                 can-edit? (:can-edit (util/when-not-loading report))
                 refresh-seconds (get-in report [:configuration "refreshSeconds"])]
             (when (and ready? (not= report-id @seeded-defaults*))
               (reset! seeded-defaults* report-id)
               (re/dispatch [:reports/seed-default-filter-params report-id]))
             (when ready?
               (let [refresh-seconds (when (some-> refresh-seconds (pos?)) refresh-seconds)]
                 (when (not= refresh-seconds @scheduled-refresh*)
                   (reset! scheduled-refresh* refresh-seconds)
                   (if refresh-seconds
                     (re/dispatch [:reports/set-refresh-timer refresh-seconds])
                     (re/dispatch [:reports/clear-refresh-timer])))))
             [l/logged-in-with-header-nav-vi
              (merge args {:concept-dropdown? true
                           :location-dropdown? (boolean (:location filters))
                           :crew-type-radio? (boolean (:crew-type filters))
                           :timeframe-dropdown? (boolean (:date filters))}
                     (when (and (not-empty target-concept-ids)
                                (not @nav-takeover-route?-))
                       {:concept-dropdown-config {:filter-concept-ids target-concept-ids}}))
              [l/standard-content-container {:id data/pdf-capture-element-id
                                             :title [:div {:style {:display :flex
                                                                   :flex-wrap :wrap
                                                                   :justify-content :space-between
                                                                   :gap 8}}
                                                     [:span {:style {:color :inherit}}
                                                      (:title (util/when-not-loading report))]
                                                     [:div {:data-html2canvas-ignore "true"
                                                            :style {:display :flex
                                                                    :flex-wrap :wrap
                                                                    :align-items :center
                                                                    :gap 8}}
                                                      #_(when (and @admin?- ready?)
                                                          [alc/up-button {:icon-type :QuestionmarkCircleIcon
                                                                          :on-click #(re/dispatch [:ai-agent/open-report-chat {:report-id report-id}])}
                                                           "Ask a Question"])
                                                      (when ready?
                                                        (let [generating? @(re/subscribe [:reports/generating-pdf?-])]
                                                          [alc/up-button {:icon-type :DownloadIcon
                                                                          :loading? generating?
                                                                          :on-click #(re/dispatch [:reports/download-pdf {:title (:title (util/when-not-loading report))}])}
                                                           "Download PDF"]))
                                                      (when all-reports-button?
                                                        [alc/up-button {:icon-type :TxtListBulletedIcon
                                                                        :on-click #(re/dispatch [:reports/open-list])}
                                                         "All Reports"])
                                                      (when (and @edit-route-access?- ready? can-edit?)
                                                        [alc/up-call-to-action-button {:icon-type :EditIcon
                                                                                       :loading? @(re/subscribe [:reports/creating-edit-session?-])
                                                                                       :on-click #(re/dispatch [:reports/edit-report report-id])}
                                                         "Edit"])]]
                                             :description (:description (util/when-not-loading report))
                                             :sticky-header? true}
               (cond
                 (or loading? (nil? report)) [empty-state/loading-data-card]
                 (empty? report) [empty-state/no-data-card
                                  {:text "Report not found"
                                   :material-icon-type :inventory_2}]
                 :else [report-details (assoc report :report-filters (or filters
                                                                         (data/report-filters (get-in report [:configuration "filters"]))))])]])))})))
